interface ContentfulEntry {
  __typename: string
  key?: string
  value?: ContentfulValue
}

interface ContentfulValue {
  __typename: string
  stringValue?: string
  booleanValue?: boolean
  intValue?: number
  floatValue?: number
  mimeType?: string
  title?: string
  url?: string
  imageUrl?: string | null
  key?: string
  entries?: ContentfulEntry[]
  value?: ContentfulValue
  values?: ContentfulValue[]
}

/**
 * All available contentful template types
 */
export interface ContentfulTemplateMap {
  aboutUs: string
  alta: string
  bannerVariations: string
  brandsPage: string
  brooksPathSetup: string
  bubbaPathLayout: string
  category: string
  chatTeaser: string
  childCategory: string
  deal: string
  engagementModal: string
  experts: string
  expertsPage: string
  expertLoggedOutBar: string
  faq: string
  fullPageTakeover: string
  fullPageTakeoverTeaser: string
  homePage: string
  instagramPosts: string
  itineraryPage: string
  itineraryList: string
  keystone: string
  landingPage: string
  loggedInHomeHero: string
  mammoth: string
  northstar: string
  openGraphTemplate: string
  orderBanner: string
  pathCallout: string
  productFixedBar: string
  productSplashQuiz: string
  quizContainerHeader: string
  returns: string
  roryPathLayout: string
  searchSplashQuiz: string
  searchFullPageTakeover: string
  smsModal: string
  specialPricingPlacement: string
  squaw: string
  teamPage: string
  topicPage: string
  vail: string
}

type StringTransformer = (s: string) => string

function extractContentfulValue(
  value: ContentfulValue,
  transform?: StringTransformer
): ContentfulEntry {
  let retValue: any

  switch (value.__typename) {
    case 'ContentBooleanValue':
      retValue = value.booleanValue
      break

    case 'ContentStringValue':
      retValue = transform && value.stringValue ? transform(value.stringValue) : value.stringValue
      break

    case 'ContentIntValue':
      retValue = value.intValue
      break

    case 'ContentFloatValue':
      retValue = value.floatValue
      break

    case 'ContentAsset':
      retValue = {
        mimeType: value.mimeType,
        title: value.title,
        imageUrl: value.imageUrl || value.url
      }
      break

    case 'ContentValueArray':
      if (value.values) {
        retValue = value.values.map(entry => {
          return extractContentfulValue(entry, transform)
        })
      }
      break

    case 'ContentObjectEntry':
      if (value.value) {
        retValue = extractContentfulValue(value.value, transform)
      }
      break

    case 'ContentObject':
      if (value.entries) {
        retValue = value.entries.reduce(
          (accu, value) => {
            if (!value.key || !value.value) {
              return {}
            }

            accu[value.key] = extractContentfulValue(value.value, transform)

            return accu
          },
          {} as { [key: string]: ContentfulEntry }
        )
      }
      break
  }

  return retValue
}

export function createContentfulObject(data: ContentfulEntry[], transform?: StringTransformer) {
  let contentfulObject: {
    [key: string]: any
  } = {}

  data.forEach((entry: ContentfulValue) => {
    if (!entry.key || !entry.value) {
      return
    }

    contentfulObject[entry.key] = extractContentfulValue(entry.value, transform)
  })

  return contentfulObject
}

export function getSpaceFromTemplate(template: keyof ContentfulTemplateMap) {
  /**
   * This is a map replicated in contentful so we can have a string
   * to space ID mapping. This may get more repetitive if we merge in
   * about sections, FAQ, etc. into this map.
   */
  const templateMap = {
    aboutUs: 'dk394eozxr14',
    alta: '7a5k9v6bcoox',
    bannerVariations: 'i2yczzl3oo44',
    brandsPage: 'herory2ot5y9',
    brooksPathSetup: 'qt3sknillzby',
    bubbaPathLayout: 'oece78ox8ged',
    category: 'qt3sknillzby',
    chatTeaser: 'qt3sknillzby',
    childCategory: 'qt3sknillzby',
    deal: 'qt3sknillzby',
    engagementModal: 'qt3sknillzby',
    experts: 'qt3sknillzby',
    expertsPage: 'herory2ot5y9',
    expertLoggedOutBar: 'qt3sknillzby',
    faq: 'qt3sknillzby',
    fullPageTakeover: 'qt3sknillzby',
    fullPageTakeoverTeaser: 'qt3sknillzby',
    homePage: 'u9rr7mrf8o2f',
    instagramPosts: 'dk394eozxr14',
    itineraryPage: 'i2yczzl3oo44',
    itineraryList: 'i2yczzl3oo44',
    keystone: 'ddf30wlthxsf',
    landingPage: 'nnjady6pcff1',
    loggedInHomeHero: 'u9rr7mrf8o2f',
    mammoth: 'oece78ox8ged',
    northstar: 'nnjady6pcff1',
    openGraphTemplate: 'qt3sknillzby',
    orderBanner: 'dk394eozxr14',
    pathCallout: 'qt3sknillzby',
    productFixedBar: 'qt3sknillzby',
    productSplashQuiz: 'qt3sknillzby',
    quizContainerHeader: 'qt3sknillzby',
    returns: 'qt3sknillzby',
    roryPathLayout: 's5dn77b34rne',
    searchSplashQuiz: 'qt3sknillzby',
    searchFullPageTakeover: 'qt3sknillzby',
    smsModal: 'axtj9owkt1b6',
    specialPricingPlacement: 'qt3sknillzby',
    squaw: 'mm3kzhdnah4i',
    teamPage: 'dk394eozxr14',
    topicPage: 'axtj9owkt1b6',
    vail: 'u63pa4t97nuv'
  }

  if (!templateMap[template]) {
    throw new Error('Undefined Contentful space referenced')
  }

  return templateMap[template]
}
